import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, Mail, Lock, LogIn } from 'lucide-react';

const ADMIN_EMAIL = 'max@maxvalle.it';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in as admin
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.email === ADMIN_EMAIL) {
        navigate('/admin/dashboard');
      }
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if email is admin email
    if (email.toLowerCase() !== ADMIN_EMAIL) {
      toast.error('Accesso riservato solo all\'amministratore');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.toLowerCase(),
        password,
      });

      if (response.data.success) {
        // Double check it's the admin
        if (response.data.user.email === ADMIN_EMAIL) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          toast.success('Accesso admin effettuato');
          // Force page reload to update auth state
          window.location.href = '/admin/dashboard';
        } else {
          toast.error('Accesso riservato solo all\'amministratore');
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Credenziali non valide';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D94E1F]/10 rounded-full mb-4">
              <Shield className="w-8 h-8 text-[#D94E1F]" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Pannello Admin
            </h1>
            <p className="text-slate-500 mt-2">Accesso riservato all'amministratore</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@email.com"
                className="mt-1.5 h-12 rounded-xl"
                required
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-12 rounded-xl"
                required
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl text-base font-medium"
              data-testid="admin-login-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Accesso in corso...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Accedi al Pannello
                </span>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              Area riservata - SpesAria Admin
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
